import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Code, History, RotateCcw, ChevronDown, Loader2 } from 'lucide-react';

// Batch 3 — Raw Code page editor (Sites module). Three monospace tabs
// (HTML / CSS / JS) bound to value.{html,css,js}; merges back through onChange.
// A Revisions popover lists published snapshots (lazy-loaded via onLoadRevisions)
// with a per-row Restore that copies into the EDITABLE raw_code (admin re-Publishes).
const TABS = [
    ['html', 'HTML'],
    ['css', 'CSS'],
    ['js', 'JS'],
];

const PLACEHOLDERS = {
    html: '<!-- Your HTML markup -->',
    css: '/* Your CSS — applied to the published page */',
    js: '// Your JavaScript — runs as-is on the published page',
};

export default function RawCodeEditor({ value, onChange, revisions, onLoadRevisions, onRestore, readOnly }) {
    const [tab, setTab] = useState('html');
    const [revOpen, setRevOpen] = useState(false);
    const [loadingRevs, setLoadingRevs] = useState(false);
    const code = value || { html: '', css: '', js: '' };

    const setField = (field, v) => onChange({ ...code, [field]: v });

    const openRevisions = async () => {
        const next = !revOpen;
        setRevOpen(next);
        if (next && onLoadRevisions) {
            setLoadingRevs(true);
            try { await onLoadRevisions(); } finally { setLoadingRevs(false); }
        }
    };

    const revList = revisions || [];

    return (
        <div className="h-full flex flex-col bg-white">
            {/* Toolbar: tabs + revisions */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-stone-200 flex-shrink-0">
                <Code className="w-4 h-4 text-stone-500" />
                <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-0.5">
                    {TABS.map(([key, label]) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => setTab(key)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                tab === key ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <div className="ml-auto relative">
                    <Button variant="outline" size="sm" onClick={openRevisions} className="text-xs">
                        <History className="w-3.5 h-3.5 mr-1" /> Revisions
                        <ChevronDown className="w-3 h-3 ml-1" />
                    </Button>
                    {revOpen && (
                        <>
                            <div className="fixed inset-0 z-30" onClick={() => setRevOpen(false)} />
                            <div className="absolute top-full right-0 mt-1 w-72 max-h-80 overflow-y-auto bg-white border border-stone-200 rounded-lg shadow-lg z-40 py-1">
                                {loadingRevs ? (
                                    <div className="flex items-center justify-center py-6 text-stone-400">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    </div>
                                ) : revList.length === 0 ? (
                                    <p className="text-xs text-stone-400 text-center py-6 px-3">No published revisions yet.</p>
                                ) : (
                                    revList.map((r) => (
                                        <div key={r.index} className="flex items-center gap-2 px-3 py-2 hover:bg-stone-50">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-medium text-stone-700 truncate">
                                                    {r.version ? `v${r.version}` : `Revision ${r.index + 1}`}
                                                </p>
                                                {r.published_at && (
                                                    <p className="text-[10px] text-stone-400 truncate">
                                                        {new Date(r.published_at).toLocaleString()}
                                                        {r.published_by ? ` · ${r.published_by}` : ''}
                                                    </p>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => { onRestore?.(r.index); setRevOpen(false); }}
                                                className="flex items-center gap-1 text-[11px] font-medium text-stone-500 hover:text-stone-900 px-1.5 py-1 rounded hover:bg-stone-100"
                                            >
                                                <RotateCcw className="w-3 h-3" /> Restore
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Editor */}
            <div className="flex-1 overflow-hidden p-4 flex flex-col">
                <textarea
                    value={code[tab] || ''}
                    onChange={(e) => setField(tab, e.target.value)}
                    readOnly={readOnly}
                    spellCheck={false}
                    placeholder={PLACEHOLDERS[tab]}
                    className="flex-1 w-full resize-none rounded-lg bg-stone-900 text-stone-100 font-mono text-sm leading-relaxed p-4 border border-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-600"
                    style={{ tabSize: 2 }}
                    rows={24}
                />
                <p className="text-[10px] text-stone-400 mt-2 flex items-center gap-1">
                    <Code className="w-3 h-3" /> Scripts run as-is on the published page — they are not sanitized.
                </p>
            </div>
        </div>
    );
}
